/**
 * The "start a fresh conversation" signal — SMB-4110.
 *
 * ── Why this needs a signal at all ──────────────────────────────────────────
 * The conversation does not live in this app. It lives in the embedded
 * traveller app inside `TravelAppFrame`, and the portal's only levers on it are
 * the frame's `src`, the identity it posts in, and a small postMessage
 * vocabulary. None of those fire on a navigation to the path the frame is
 * already showing: `TravelAppFrame` explicitly skips its `zenvoya:set-surface`
 * message when `surface === shownSurfaceRef.current`, deliberately, so that
 * ordinary re-renders do not wipe a chat in progress.
 *
 * That is correct for navigation and wrong for a reset, so a reset has to be
 * distinguishable from arriving at the screen normally. This is that
 * distinction: a NONCE carried in react-router's location state.
 *
 * ── Why location state, and not a query param or a context ──────────────────
 * A query param (`?new=1`) would be visible, survive a copy-pasted link, and
 * re-fire the reset on every reload of that URL — a bookmark that silently
 * destroys a conversation. A context would work but means a provider spanning
 * the sidebar and the page for one integer.
 *
 * Location state is invisible, scoped to one history entry, and — because the
 * value is new every call — changes even when the path does not, which is the
 * single property the whole feature rests on. Two clicks in a row are two
 * distinct locations, so the second one resets too.
 *
 * ── Reading it ──────────────────────────────────────────────────────────────
 * Each portal's `pages/travel/TravelPage.jsx` passes the nonce to
 * `TravelAppFrame` as `resetNonce`, which folds it into the iframe's `key`. A
 * changed key replaces the document outright, so the new conversation is a
 * genuinely new page load rather than a clear the traveller app has to be
 * trusted to perform.
 *
 * It lives beside `TravelAppFrame` rather than under either portal because
 * BOTH sidebars raise the signal and both travel pages read it — the CRM's
 * `SidebarNavItem` and the partner sidebar's `SidebarItem`. One definition of
 * the state key is what keeps the two from drifting.
 */

/** The state key. Namespaced so it cannot collide with other navigation state. */
export const NEW_CHAT_STATE_KEY = 'zvNewChat';

/**
 * Fresh navigation state for a "new chat" click.
 *
 * `Date.now()` is enough: two clicks inside the same millisecond would produce
 * the same nonce and the second would not reset, but that is a double-click on
 * a control whose first click already reloaded the frame — there is nothing
 * left for the second to do.
 */
export const newChatNavState = () => ({ [NEW_CHAT_STATE_KEY]: Date.now() });

/** The nonce carried by a location, or `0` when it arrived by ordinary means. */
export const readNewChatNonce = (state) => {
  const value = state?.[NEW_CHAT_STATE_KEY];
  return typeof value === 'number' ? value : 0;
};
